/**
 * useScrollRestore — remembers the window scroll offset per view key and
 * re-applies it once the view's content has rendered.
 *
 * Restoring before `ready` would be clamped to the short skeleton height, so
 * the effect waits for the caller to report that the list is on screen.
 *
 * Persistence is time-throttled (200ms) so `sessionStorage.setItem` is not
 * called on every animation frame during a long scroll.
 */
import { useEffect, useRef } from 'react';
import { getPageScrollY } from '../../utils/pageScrollLock';
import { readScrollOffset, writeScrollOffset } from './groupsViewState';

const PERSIST_THROTTLE_MS = 200;

export function useScrollRestore(key: string, ready: boolean): void {
  const restoredKey = useRef<string | null>(null);

  useEffect(() => {
    let lastPersist = 0;
    let pending: ReturnType<typeof setTimeout> | null = null;

    const persist = (offset: number) => {
      if (restoredKey.current !== key) return;
      const now = Date.now();
      const elapsed = now - lastPersist;
      if (elapsed >= PERSIST_THROTTLE_MS) {
        lastPersist = now;
        writeScrollOffset(key, offset);
      } else if (pending === null) {
        pending = setTimeout(() => {
          pending = null;
          lastPersist = Date.now();
          writeScrollOffset(key, getPageScrollY());
        }, PERSIST_THROTTLE_MS - elapsed);
      }
    };

    // An open overlay pins the body and fires a scroll event reading 0, which
    // would persist the top of the page over the real position.
    const handleScroll = () => persist(getPageScrollY());

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (pending) clearTimeout(pending);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [key]);

  useEffect(() => {
    if (!ready || restoredKey.current === key) return;
    const target = readScrollOffset(key);
    restoredKey.current = key;
    if (target <= 0) return;
    const frame = window.requestAnimationFrame(() =>
      window.scrollTo({ top: target, behavior: 'auto' }),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [key, ready]);
}
